import React, { FC, useState, useMemo, useEffect } from "react";
import { FileTextFill, InfoCircle, Clipboard } from "react-bootstrap-icons";
import { SmartJsonRenderer } from "../../components/SmartJsonRenderer";
import { HtmlRenderer } from "../../components/htmlRenderer";
import {
  FinancialApplication,
  FinancialApplicationCreditReport,
} from "../../models/applications/applicationInterfaces";
import { useSelector } from "react-redux";
import { selectApplicationDetail } from "../../models/applications/applicationDetailSlice";
import ApplicantSelector from "../../components/ui/applicantSelector";
import CreditBureauCard from "../../components/CreditBureauCard/CreditBureauCard";
import { formatDate } from "../../utils/helpers";
import JsonDisplay from "../../components/ui/JsonDisplay";
import { message } from "antd";

interface CreditReportsProps {
  creditReportsData?: Array<FinancialApplicationCreditReport>;
  applicationsData?: FinancialApplication;
}

export const CreditReports: FC<CreditReportsProps> = ({
  creditReportsData,
  applicationsData,
}) => {
  const [activeApplicantTab, setActiveApplicantTab] = useState("applicant");
  const [selectedReportId, setSelectedReportId] = useState<string>("");
  const [dataViewMode, setDataViewMode] = useState<"data" | "report" | "raw">(
    "report"
  );

  const application = useSelector(selectApplicationDetail);
  const permissions = useMemo(
    () => application.application?.permissions,
    [application]
  );

  // Guard against users without permission
  if (!permissions?.includes("view-application-credit-report")) {
    return (
      <div>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  // If no credit report data
  if (!creditReportsData || creditReportsData.length === 0) {
    return (
      <div>
        <p className="mt-2">
          No credit report data was provided for this application.
        </p>
      </div>
    );
  }

  // Group reports by scope
  const reportsByScope = creditReportsData.reduce(
    (
      acc: Record<
        FinancialApplicationCreditReport["scope"],
        Array<FinancialApplicationCreditReport>
      >,
      report: FinancialApplicationCreditReport
    ) => {
      const scope = report.scope || "application";
      if (!acc[scope]) {
        acc[scope] = [];
      }
      acc[scope].push(report);
      return acc;
    },
    {}
  );

  // Sort reports by bureau name, then by receivedAt timestamp
  const sortReports = (reports: FinancialApplicationCreditReport[]) => {
    return reports.sort((a, b) => {
      // First sort by bureau name
      const bureauCompare = (a.bureau || "").localeCompare(b.bureau || "");
      if (bureauCompare !== 0) return bureauCompare;

      // Then sort by receivedAt timestamp (most recent first)
      const aDate = a.receivedAt;
      const bDate = b.receivedAt;
      const dateA = aDate ? new Date(aDate).getTime() : 0;
      const dateB = bDate ? new Date(bDate).getTime() : 0;
      return dateB - dateA;
    });
  };

  // For applicant scope, include both "applicant" and "application" reports
  const applicantReports = sortReports([
    ...(reportsByScope["applicant"] || []),
    ...(reportsByScope["application"] || []),
  ]);
  const coApplicantReports = sortReports(reportsByScope["co-applicant"] || []);

  // Determine which tabs to show
  const hasApplicantReports = applicantReports.length > 0;
  const hasCoApplicantReports = coApplicantReports.length > 0;

  // Get the current reports based on selected applicant. Flows that pull only
  // the co-applicant (e.g. youth flows pulling the guardian) have no applicant
  // reports, and the selector is hidden, so fall back to the co-applicant.
  const showCoApplicant =
    activeApplicantTab !== "applicant" || !hasApplicantReports;
  const currentReports = showCoApplicant
    ? coApplicantReports
    : applicantReports;

  // Set initial selected report if not set
  if (currentReports.length > 0 && !selectedReportId) {
    setSelectedReportId(currentReports[0].id);
  }

  // Get the current report based on selected report ID
  const currentReport = currentReports.find(
    (r: FinancialApplicationCreditReport) => r.id === selectedReportId
  );

  // Auto-switch to appropriate view when changing reports
  useEffect(() => {
    if (currentReport) {
      // Default to "report" if HTML exists, otherwise "data"
      if (currentReport.html) {
        setDataViewMode("report");
      } else if (currentReport.data) {
        setDataViewMode("data");
      }
    }
  }, [selectedReportId]);

  // Determine if we need to show data/report toggle
  // Show tabs only when there are multiple view options:
  // - If only data exists: show toggle (Data View and Raw View)
  // - If both data and html exist: show toggle (all three views)
  // - If only html exists: hide toggle (only one view available)
  const showDataReportToggle = Boolean(currentReport?.data);

  // Copy to clipboard function
  const copyToClipboard = async () => {
    if (currentReport?.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(currentReport.data, null, 2)
        );
        message.success("Copied to clipboard!");
      } catch (err) {
        console.error("Failed to copy:", err);
        message.error("Failed to copy to clipboard");
      }
    }
  };

  return (
    <div>
      {/* Applicant/Co-applicant Tabs (if both exist) */}
      {hasApplicantReports && hasCoApplicantReports && (
        <div className="mb-4">
          <ApplicantSelector
            activeTab={activeApplicantTab}
            onTabClick={(tab: string) => {
              setActiveApplicantTab(tab);
              const reports =
                tab === "applicant" ? applicantReports : coApplicantReports;
              setSelectedReportId(reports[0]?.id || "");
            }}
            applicantName={`${applicationsData?.applicant?.firstName} ${applicationsData?.applicant?.lastName}`}
            coApplicantName={`${applicationsData?.coApplicant?.firstName} ${applicationsData?.coApplicant?.lastName}`}
          />
        </div>
      )}

      {/* Credit Bureau Cards */}
      <div className="d-flex flex-wrap" style={{ gap: "1rem" }}>
        {currentReports.map((report: FinancialApplicationCreditReport) => (
          <CreditBureauCard
            key={report.id}
            report={report}
            isSelected={selectedReportId === report.id}
            onClick={() => setSelectedReportId(report.id)}
            formatDate={(dateString: string) => formatDate(dateString) || "N/A"}
          />
        ))}
      </div>

      {/* Data/Report Toggle and Content */}
      {currentReport && (
        <>
          {/* Title and Data/Report Toggle */}
          <div className="mb-3 mt-5 d-flex align-items-center justify-content-between">
            <h1 className="text-tertiary h2 m-0 font-weight-bold">
              <FileTextFill className="mr-2" />
              {currentReport.product || currentReport.bureau}
            </h1>

            <div
              className="d-flex align-items-center"
              style={{ gap: "0.5rem" }}
            >
              {dataViewMode === "raw" && currentReport.data && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={copyToClipboard}
                  title="Copy to Clipboard"
                >
                  <Clipboard />
                </button>
              )}

              {showDataReportToggle && (
                <div className="btn-group" role="group">
                  {currentReport.html && (
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        dataViewMode === "report"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setDataViewMode("report")}
                    >
                      Report View
                    </button>
                  )}
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      dataViewMode === "data"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => setDataViewMode("data")}
                  >
                    Data View
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      dataViewMode === "raw"
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => setDataViewMode("raw")}
                  >
                    Raw View
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div
            className="bg-white border p-4 rounded"
            style={{ overflowX: "hidden" }}
          >
            {dataViewMode === "data" && currentReport.data ? (
              <SmartJsonRenderer data={currentReport.data} />
            ) : dataViewMode === "report" && currentReport.html ? (
              <HtmlRenderer htmlString={currentReport.html} />
            ) : dataViewMode === "raw" && currentReport.data ? (
              <JsonDisplay json={currentReport.data} />
            ) : currentReport.data ? (
              <SmartJsonRenderer data={currentReport.data} />
            ) : currentReport.html ? (
              <HtmlRenderer htmlString={currentReport.html} />
            ) : (
              <p>No data available for this credit report.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CreditReports;
